import React from 'react';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { ICustomizableElement } from '@insurancemenu/imenu-components-lib/dist/types';
import { observer } from 'mobx-react-lite';
import { colors } from '@insurancemenu/imenu-components-lib';
import { InfoButtonFilled } from '@insurancemenu/imenu-components-lib/dist/icons';
import {
  IconStyled,
  PRODUCT_LINE_CHIP_HEIGHT,
  ProductLineItemStyled,
  ProductLineSkeletonStyled,
  ProductLinesContainerStyled,
  ProductLinesHintStyled,
  ProductLinesListStyled,
  ProductLinesSkeletonListStyled,
  SectionStyled,
  SubsectionTitleStyled,
} from './styles';
import {
  AVAILABLE_PRODUCT_LINES_HINT_TEST_ID,
  AVAILABLE_PRODUCT_LINES_LIST_TEST_ID,
  AVAILABLE_PRODUCT_LINES_SKELETON_TEST_ID,
} from '../constants';

/** Skeleton widths that imitate real product line chip labels. */
const PRODUCT_LINE_SKELETON_WIDTHS = ['14rem', '11rem', '16rem', '12rem', '10rem'];

interface ProductLineItem {
  productLineId: ProductLineId;
  isAvailableWithCurrentConfiguration: boolean;
  isLocked?: boolean;
}

export interface Props extends ICustomizableElement {
  domainProductLineItems: ProductLineItem[];
  isLoading?: boolean;
}

interface UnavailableProductLinesGroupProps {
  productLineItems: ProductLineItem[];
  title: string;
  iconColor: string;
}

const UnavailableProductLinesGroup: React.FC<UnavailableProductLinesGroupProps> = ({
  productLineItems,
  title,
  iconColor,
}) => {
  if (productLineItems.length === 0) {
    return null;
  }

  return (
    <SectionStyled>
      <SubsectionTitleStyled
        text={
          <>
            <InfoButtonFilled color={iconColor} width="2.4rem" height="2.4rem" /> {title}
          </>
        }
      />
      <ProductLinesListStyled data-test-id={AVAILABLE_PRODUCT_LINES_LIST_TEST_ID}>
        {productLineItems.map((productLineItem) => (
          <ProductLineItemStyled key={productLineItem.productLineId} $unavailable>
            {PRODUCT_LINES_CONTENT_MAP[productLineItem.productLineId]?.icon('1.8rem')}
            {PRODUCT_LINES_CONTENT_MAP[productLineItem.productLineId]?.label}
          </ProductLineItemStyled>
        ))}
      </ProductLinesListStyled>
    </SectionStyled>
  );
};

const DomainProductLines: React.FC<Props> = observer(({ domainProductLineItems, className, isLoading = false }) => {
  const availableProductLineItems = domainProductLineItems.filter(
    (productLineItem) => productLineItem.isAvailableWithCurrentConfiguration,
  );
  const unavailableProductLineItems = domainProductLineItems.filter(
    (productLineItem) => !productLineItem.isAvailableWithCurrentConfiguration,
  );

  if (isLoading) {
    return (
      <ProductLinesSkeletonListStyled data-test-id={AVAILABLE_PRODUCT_LINES_SKELETON_TEST_ID}>
        {PRODUCT_LINE_SKELETON_WIDTHS.map((width) => (
          <ProductLineSkeletonStyled key={width} width={width} height={PRODUCT_LINE_CHIP_HEIGHT} />
        ))}
      </ProductLinesSkeletonListStyled>
    );
  }

  if (domainProductLineItems.length < 1) {
    return (
      <ProductLinesHintStyled data-test-id={AVAILABLE_PRODUCT_LINES_HINT_TEST_ID}>
        <IconStyled color={colors.yellow[600]} width="2.4rem" height="2.4rem" /> Complete the required fields to see
        available product lines.
      </ProductLinesHintStyled>
    );
  }

  return (
    <ProductLinesContainerStyled className={className}>
      {availableProductLineItems.length > 0 && (
        <ProductLinesListStyled data-test-id={AVAILABLE_PRODUCT_LINES_LIST_TEST_ID}>
          {availableProductLineItems.map((productLineItem) => (
            <ProductLineItemStyled key={productLineItem.productLineId}>
              {PRODUCT_LINES_CONTENT_MAP[productLineItem.productLineId]?.icon('1.8rem')}
              {PRODUCT_LINES_CONTENT_MAP[productLineItem.productLineId]?.label}
            </ProductLineItemStyled>
          ))}
        </ProductLinesListStyled>
      )}
      <UnavailableProductLinesGroup
        productLineItems={unavailableProductLineItems}
        title="Unavailable For Current Configuration"
        iconColor={colors.yellow[600]}
      />
    </ProductLinesContainerStyled>
  );
});

export default DomainProductLines;
